<div class="reminders-hub-container">

  <!-- ========================================================================= -->
  <!-- 1. HEADER & KPI STAT CARDS -->
  <!-- ========================================================================= -->
  @if (showHeaderCard) {
    <div class="hub-header-card app-card p-3 mb-3">
      <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h5 class="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
            <i class="bi bi-bell-fill text-primary"></i>
            <span>Follow-Ups & Reminders Hub</span>
          </h5>
          <span class="text-muted fs-8">
            Command center for scheduled sales calls, field follow-ups, demos, and pipeline commitments
          </span>
        </div>
        <div class="d-flex align-items-center gap-2">
          <button 
            type="button" 
            class="btn btn-sm btn-light border d-inline-flex align-items-center" 
            (click)="loadSummary(); loadReminders()" 
            [disabled]="loading || summaryLoading"
            title="Refresh reminders and live counters">
            <i class="bi bi-arrow-clockwise me-1" [class.spin]="loading || summaryLoading"></i>
            <span>Refresh</span>
          </button>
          <button 
            type="button" 
            class="btn btn-sm btn-primary d-inline-flex align-items-center shadow-sm"
            (click)="openCreateModal(createModal)">
            <i class="bi bi-plus-circle-fill me-1"></i>
            <span>New Reminder</span>
          </button>
        </div>
      </div>

      <!-- Live Stat Counter Cards -->
      <div class="row g-2">
        <!-- 1. Overdue -->
        <div class="col-6 col-md">
          <div 
            class="stat-counter-card stat-card-danger cursor-pointer"
            [class.active-stat]="activePreset === 'overdue'"
            (click)="selectPreset('overdue')">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <span class="stat-label text-danger">Overdue</span>
                <h4 class="stat-value text-danger mb-0 mt-1">
                  {{ summary.overdue || 0 }}
                </h4>
              </div>
              <div class="stat-icon-wrap bg-danger-subtle text-danger">
                <i class="bi bi-exclamation-octagon-fill"></i>
              </div>
            </div>
            <div class="stat-subtext text-muted fs-9 mt-1">
              Immediate action needed
            </div>
          </div>
        </div>

        <!-- 2. Due Today -->
        <div class="col-6 col-md">
          <div 
            class="stat-counter-card stat-card-warning cursor-pointer"
            [class.active-stat]="activePreset === 'today'"
            (click)="selectPreset('today')">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <span class="stat-label text-warning-emphasis">Due Today</span>
                <h4 class="stat-value text-warning-emphasis mb-0 mt-1">
                  {{ summary.today || 0 }}
                </h4>
              </div>
              <div class="stat-icon-wrap bg-warning-subtle text-warning">
                <i class="bi bi-calendar-event-fill"></i>
              </div>
            </div>
            <div class="stat-subtext text-muted fs-9 mt-1">
              Scheduled for today
            </div>
          </div>
        </div>

        <!-- 3. Upcoming 7 Days -->
        <div class="col-6 col-md">
          <div 
            class="stat-counter-card stat-card-info cursor-pointer"
            [class.active-stat]="activePreset === 'upcoming'"
            (click)="selectPreset('upcoming')">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <span class="stat-label text-info-emphasis">Next 7 Days</span>
                <h4 class="stat-value text-info mb-0 mt-1">
                  {{ summary.upcoming_7_days || 0 }}
                </h4>
              </div>
              <div class="stat-icon-wrap bg-info-subtle text-info">
                <i class="bi bi-calendar2-week-fill"></i>
              </div>
            </div>
            <div class="stat-subtext text-muted fs-9 mt-1">
              Upcoming pipeline
            </div>
          </div>
        </div>

        <!-- 4. Completed -->
        <div class="col-6 col-md">
          <div 
            class="stat-counter-card stat-card-success cursor-pointer"
            [class.active-stat]="activePreset === 'completed'"
            (click)="selectPreset('completed')">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <span class="stat-label text-success">Completed</span>
                <h4 class="stat-value text-success mb-0 mt-1">
                  {{ summary.completed || 0 }}
                </h4>
              </div>
              <div class="stat-icon-wrap bg-success-subtle text-success">
                <i class="bi bi-check-circle-fill"></i>
              </div>
            </div>
            <div class="stat-subtext text-muted fs-9 mt-1">
              Follow-ups closed
            </div>
          </div>
        </div>

        <!-- 5. All Pending -->
        <div class="col-12 col-md">
          <div 
            class="stat-counter-card stat-card-secondary cursor-pointer"
            [class.active-stat]="activePreset === ''"
            (click)="selectPreset('')">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <span class="stat-label text-secondary">All Pending</span>
                <h4 class="stat-value text-dark mb-0 mt-1">
                  {{ summary.total_pending || 0 }}
                </h4>
              </div>
              <div class="stat-icon-wrap bg-secondary-subtle text-secondary">
                <i class="bi bi-hourglass-split"></i>
              </div>
            </div>
            <div class="stat-subtext text-muted fs-9 mt-1">
              Total active queue
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- ========================================================================= -->
  <!-- 2. PRESET SEGMENT NAVIGATION & FILTER TOOLBAR -->
  <!-- ========================================================================= -->
  <div class="app-card p-3 mb-3">
    <!-- Preset Segment Buttons -->
    <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 pb-3 border-bottom mb-3">
      <nav class="nav-segment-control" aria-label="Reminder Preset Tabs">
        @for (tab of presetTabs; track tab.id) {
          <button
            type="button"
            class="nav-segment-item"
            [class.active]="activePreset === tab.id"
            (click)="selectPreset(tab.id)">
            <i [class]="tab.icon" class="me-1"></i>
            <span>{{ tab.label }}</span>
            <span class="badge ms-1" [ngClass]="tab.badgeClass">
              {{ summary[tab.badgeKey] || 0 }}
            </span>
          </button>
        }
      </nav>

      @if (!showHeaderCard) {
        <button 
          type="button" 
          class="btn btn-sm btn-primary d-inline-flex align-items-center shadow-sm"
          (click)="openCreateModal(createModal)">
          <i class="bi bi-plus-circle-fill me-1"></i>
          <span>New Reminder</span>
        </button>
      }
    </div>

    <!-- Filters Bar -->
    <div class="row g-2 align-items-center">
      <!-- Search Input -->
      <div class="col-md-3">
        <div class="input-group input-group-sm">
          <span class="input-group-text bg-white border-end-0 text-muted">
            <i class="bi bi-search"></i>
          </span>
          <input
            type="text"
            class="form-control form-control-sm border-start-0"
            placeholder="Search title, contact, rep..."
            [ngModel]="searchQuery"
            (ngModelChange)="onSearch($event)">
          @if (searchQuery) {
            <button class="btn btn-sm btn-outline-secondary border-start-0" type="button" (click)="onSearch('')">
              <i class="bi bi-x"></i>
            </button>
          }
        </div>
      </div>

      <!-- Assigned To Rep Filter (Admin/Supervisor or all) -->
      <div class="col-md-3">
        <select 
          class="form-select form-select-sm"
          [ngModel]="selectedAssignee"
          (ngModelChange)="onAssigneeChange($event)">
          <option [ngValue]="null">All Assigned Reps</option>
          @for (u of usersList; track u.id) {
            <option [ngValue]="u.id">
              {{ u.firstname }} {{ u.lastname || '' }} ({{ u.email || ('ID: ' + u.id) }})
            </option>
          }
        </select>
      </div>

      <!-- Date Range (From & To) -->
      <div class="col-md-4">
        <div class="d-flex align-items-center gap-1">
          <span class="fs-9 text-muted flex-shrink-0">From:</span>
          <input 
            type="date" 
            class="form-control form-control-sm" 
            [(ngModel)]="fromDate" 
            (change)="onDateFilterChange()">
          <span class="fs-9 text-muted flex-shrink-0">To:</span>
          <input 
            type="date" 
            class="form-control form-control-sm" 
            [(ngModel)]="toDate" 
            (change)="onDateFilterChange()">
        </div>
      </div>

      <!-- Clear Filters & Count -->
      <div class="col-md-2 text-md-end">
        @if (selectedAssignee || fromDate || toDate || searchQuery) {
          <button 
            type="button" 
            class="btn btn-xs btn-outline-danger d-inline-flex align-items-center"
            (click)="clearFilters()">
            <i class="bi bi-x-circle me-1"></i> Reset Filters
          </button>
        } @else {
          <span class="fs-8 text-muted">
            Total: <strong>{{ totalRecords }}</strong>
          </span>
        }
      </div>
    </div>
  </div>

  <!-- ========================================================================= -->
  <!-- 3. REMINDERS TABLE / CONTENT -->
  <!-- ========================================================================= -->
  <div class="app-card">
    <!-- Loading State -->
    @if (loading) {
      <div class="text-center py-5 text-muted">
        <div class="spinner-border text-primary spinner-border-sm mb-2" role="status"></div>
        <div class="fs-7 fw-semibold text-dark">Loading reminders queue...</div>
        <div class="fs-8 text-muted">Fetching latest assignments, due dates, and action histories</div>
      </div>
    }

    <!-- Error State -->
    @if (!loading && errorMessage) {
      <div class="p-3">
        <div class="alert alert-danger d-flex align-items-center justify-content-between mb-0 py-2">
          <div class="d-flex align-items-center gap-2">
            <i class="bi bi-exclamation-triangle-fill fs-5"></i>
            <span class="fs-7">{{ errorMessage }}</span>
          </div>
          <button class="btn btn-sm btn-outline-danger" (click)="loadReminders()">Retry</button>
        </div>
      </div>
    }

    <!-- Empty State -->
    @if (!loading && !errorMessage && reminders.length === 0) {
      <div class="text-center py-5">
        <div class="empty-state-icon mb-2">
          <i class="bi bi-calendar-check text-muted fs-1 opacity-50"></i>
        </div>
        <h6 class="fw-semibold text-dark mb-1">No Reminders Found</h6>
        <p class="fs-8 text-muted mb-3" style="max-width: 420px; margin: 0 auto;">
          @if (activePreset === 'overdue') {
            Great job! No overdue follow-ups in this queue.
          } @else if (activePreset === 'today') {
            No tasks due today. Schedule upcoming calls, meetings, or customer follow-ups.
          } @else if (activePreset === 'upcoming') {
            No upcoming reminders in the next 7 days.
          } @else if (activePreset === 'completed') {
            No completed follow-ups recorded yet.
          } @else {
            All pending tasks are clear or no records match your filter criteria.
          }
        </p>
        <button class="btn btn-sm btn-primary d-inline-flex align-items-center" (click)="openCreateModal(createModal)">
          <i class="bi bi-plus-circle me-1"></i>
          <span>Schedule New Reminder</span>
        </button>
      </div>
    }

    <!-- Table View -->
    @if (!loading && !errorMessage && reminders.length > 0) {
      <div class="table-responsive">
        <table class="table app-table table-hover align-middle mb-0">
          <thead>
            <tr>
              <th scope="col" style="min-width: 170px;">Due Date & Time</th>
              <th scope="col" style="min-width: 240px;">Title & Details</th>
              <th scope="col" style="min-width: 210px;">Linked Contact / Lead</th>
              <th scope="col" style="min-width: 170px;">Assigned Rep</th>
              <th scope="col" style="min-width: 110px;">Status</th>
              <th scope="col" class="text-end" style="min-width: 190px;">Actions</th>
            </tr>
          </thead>
          <tbody>
            @for (reminder of reminders; track reminder.reminder_id) {
              <tr [class.row-overdue]="isOverdue(reminder.due_at, reminder.reminder_status)">
                
                <!-- 1. DUE DATE & TIME (Flag in red if past due) -->
                <td>
                  <div class="d-flex flex-column gap-1">
                    <div class="d-flex align-items-center gap-1">
                      <i class="bi bi-clock-history fs-8" [class.text-danger]="isOverdue(reminder.due_at, reminder.reminder_status)" [class.text-muted]="!isOverdue(reminder.due_at, reminder.reminder_status)"></i>
                      <span class="fs-7 fw-semibold" [class.text-danger]="isOverdue(reminder.due_at, reminder.reminder_status)" [class.text-dark]="!isOverdue(reminder.due_at, reminder.reminder_status)">
                        {{ reminder.due_at | date:'dd MMM yyyy, hh:mm a' }}
                      </span>
                    </div>

                    <!-- Badges for Overdue or Due Today -->
                    <div>
                      @if (isOverdue(reminder.due_at, reminder.reminder_status)) {
                        <span class="badge bg-danger text-white fs-9 px-2 py-1 rounded-pill">
                          <i class="bi bi-exclamation-octagon-fill me-1"></i>Overdue
                        </span>
                      } @else if (isDueToday(reminder.due_at, reminder.reminder_status)) {
                        <span class="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle fs-9 px-2 py-1 rounded-pill">
                          <i class="bi bi-lightning-charge-fill me-1"></i>Due Today
                        </span>
                      } @else if (reminder.reminder_status === 'completed') {
                        <span class="badge bg-success-subtle text-success fs-9 px-2 py-0 rounded">
                          Done: {{ reminder.completed_at ? (reminder.completed_at | date:'dd MMM, hh:mm a') : 'Completed' }}
                        </span>
                      }
                    </div>
                  </div>
                </td>

                <!-- 2. TITLE & DESCRIPTION (Clickable to view history modal) -->
                <td>
                  <div class="reminder-info">
                    <a 
                      href="javascript:void(0)" 
                      class="fw-bold text-dark text-decoration-none d-block fs-7 hover-primary"
                      (click)="openHistoryModal(historyModal, reminder)"
                      title="Click to view history & details">
                      {{ reminder.title }}
                    </a>
                    @if (reminder.description) {
                      <div class="text-muted fs-8 text-truncate mt-1" style="max-width: 320px;" [title]="reminder.description">
                        {{ reminder.description }}
                      </div>
                    }
                    <div class="fs-9 text-muted mt-1">
                      Created by: {{ getRepName(reminder.created_by_user) }} &bull; {{ reminder.created_at | date:'dd MMM yyyy' }}
                    </div>
                  </div>
                </td>

                <!-- 3. LINKED CONTACT / LEAD -->
                <td>
                  @if (reminder.contact) {
                    <div>
                      <a 
                        [routerLink]="['/hrm', reminder.contact.contact_type?.contact_type_code || 'customer', 'view', reminder.contact.contact_id]" 
                        class="fw-semibold text-primary text-decoration-none d-flex align-items-center gap-1 hover-underline fs-7">
                        <i class="bi bi-person-fill"></i>
                        <span>{{ reminder.contact.name }}</span>
                      </a>
                      
                      <div class="d-flex align-items-center gap-2 mt-1">
                        @if (reminder.contact.phone) {
                          <a [href]="'tel:' + reminder.contact.phone" class="text-muted fs-8 text-decoration-none d-inline-flex align-items-center">
                            <i class="bi bi-telephone-fill text-success fs-9 me-1"></i>
                            <span>{{ reminder.contact.phone }}</span>
                          </a>
                        }

                        @if (reminder.contact.contact_type) {
                          <span class="badge fs-9 py-0" [ngClass]="getContactTypeBadgeClass(reminder.contact.contact_type.contact_type_code)">
                            {{ reminder.contact.contact_type.contact_type_name }}
                          </span>
                        }
                      </div>
                    </div>
                  } @else {
                    <span class="badge bg-light text-secondary border fs-8 d-inline-flex align-items-center gap-1">
                      <i class="bi bi-briefcase text-muted"></i>
                      <span>Internal Task</span>
                    </span>
                  }
                </td>

                <!-- 4. ASSIGNED REP -->
                <td>
                  <div class="d-flex align-items-center gap-2">
                    <div class="rep-mini-avatar flex-shrink-0">
                      {{ getInitials(reminder.assigned_user?.firstname) }}
                    </div>
                    <div class="d-flex flex-column">
                      <span class="fs-7 fw-semibold text-dark">
                        {{ getRepName(reminder.assigned_user) }}
                      </span>
                      @if (reminder.assigned_user?.email) {
                        <span class="fs-9 text-muted text-truncate" style="max-width: 140px;">
                          {{ reminder.assigned_user?.email }}
                        </span>
                      }
                    </div>
                  </div>
                </td>

                <!-- 5. STATUS BADGE -->
                <td>
                  @if (reminder.reminder_status === 'pending') {
                    <span class="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle fs-8">
                      <i class="bi bi-hourglass-split me-1"></i>Pending
                    </span>
                  } @else if (reminder.reminder_status === 'completed') {
                    <span class="badge bg-success-subtle text-success border border-success-subtle fs-8">
                      <i class="bi bi-check-circle-fill me-1"></i>Completed
                    </span>
                  } @else {
                    <span class="badge bg-secondary-subtle text-secondary border border-secondary-subtle fs-8">
                      <i class="bi bi-x-circle-fill me-1"></i>Cancelled
                    </span>
                  }
                </td>

                <!-- 6. ACTIONS (Complete, Reschedule, Cancel, History) -->
                <td class="text-end">
                  <div class="d-inline-flex align-items-center gap-1">
                    @if (reminder.reminder_status === 'pending') {
                      <!-- Complete Button -->
                      <button 
                        type="button" 
                        class="btn btn-xs btn-outline-success d-inline-flex align-items-center"
                        (click)="openActionModal(actionModal, reminder, 'complete')"
                        title="Mark Complete with Outcome">
                        <i class="bi bi-check2 me-1"></i>
                        <span>Complete</span>
                      </button>

                      <!-- Reschedule Button -->
                      <button 
                        type="button" 
                        class="btn btn-xs btn-outline-primary d-inline-flex align-items-center"
                        (click)="openActionModal(actionModal, reminder, 'reschedule')"
                        title="Reschedule Follow-Up Date">
                        <i class="bi bi-calendar-range me-1"></i>
                        <span>Reschedule</span>
                      </button>

                      <!-- Cancel Button -->
                      <button 
                        type="button" 
                        class="btn btn-xs btn-outline-danger d-inline-flex align-items-center"
                        (click)="openActionModal(actionModal, reminder, 'cancel')"
                        title="Cancel Reminder">
                        <i class="bi bi-x-lg"></i>
                      </button>
                    }

                    <!-- History / Logs Button -->
                    <button 
                      type="button" 
                      class="btn btn-xs btn-light border d-inline-flex align-items-center"
                      (click)="openHistoryModal(historyModal, reminder)"
                      title="View Action History">
                      <i class="bi bi-journal-text"></i>
                    </button>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <!-- Pagination Footer -->
      <div class="card-footer bg-white p-3 border-top d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div class="d-flex align-items-center gap-2">
          <span class="fs-8 text-muted">
            Showing {{ ((currentPage - 1) * pageSize) + 1 }} to {{ Math.min(currentPage * pageSize, totalRecords) }} of {{ totalRecords }} reminders
          </span>
          <select 
            class="form-select form-select-xs w-auto fs-8" 
            [ngModel]="pageSize" 
            (ngModelChange)="onPageSizeChange($event)">
            <option [ngValue]="10">10 / page</option>
            <option [ngValue]="20">20 / page</option>
            <option [ngValue]="50">50 / page</option>
            <option [ngValue]="100">100 / page</option>
          </select>
        </div>

        @if (totalRecords > pageSize) {
          <ngb-pagination
            [collectionSize]="totalRecords"
            [page]="currentPage"
            [pageSize]="pageSize"
            [maxSize]="5"
            [rotate]="true"
            [ellipses]="false"
            [boundaryLinks]="true"
            size="sm"
            (pageChange)="onPageChange($event)">
          </ngb-pagination>
        }
      </div>
    }
  </div>

</div>


<!-- ========================================================================= -->
<!-- MODAL: CREATE NEW REMINDER -->
<!-- ========================================================================= -->
<ng-template #createModal let-modal>
  <div class="modal-header bg-light">
    <h6 class="modal-title fw-bold text-dark d-flex align-items-center gap-2 mb-0">
      <i class="bi bi-calendar-plus text-primary"></i>
      <span>Schedule New Follow-Up / Reminder</span>
    </h6>
    <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body p-4">
    @if (createErrorMessage) {
      <div class="alert alert-danger py-2 fs-8 mb-3">
        <i class="bi bi-exclamation-circle me-1"></i>{{ createErrorMessage }}
      </div>
    }

    <form (ngSubmit)="submitCreate(modal)">
      <!-- Title -->
      <div class="mb-3">
        <label class="form-label fs-8 fw-semibold text-dark">
          Title / Follow-Up Subject <span class="text-danger">*</span>
        </label>
        <input 
          type="text" 
          class="form-control form-control-sm" 
          placeholder="e.g. Discuss quote revision for brick delivery" 
          [(ngModel)]="formTitle" 
          name="formTitle" 
          required 
          autofocus>
      </div>

      <!-- Due Date & Time -->
      <div class="row g-2 mb-3">
        <div class="col-md-6">
          <label class="form-label fs-8 fw-semibold text-dark">
            Due Date & Time <span class="text-danger">*</span>
          </label>
          <input 
            type="datetime-local" 
            class="form-control form-control-sm" 
            [(ngModel)]="formDueAt" 
            name="formDueAt" 
            required>
        </div>

        <!-- Assigned Rep -->
        <div class="col-md-6">
          <label class="form-label fs-8 fw-semibold text-dark">
            Assign To Sales Rep
          </label>
          <select 
            class="form-select form-select-sm" 
            [(ngModel)]="formAssignedTo" 
            name="formAssignedTo">
            @for (u of usersList; track u.id) {
              <option [ngValue]="u.id">
                {{ u.firstname }} {{ u.lastname || '' }} ({{ u.email || ('ID: ' + u.id) }})
              </option>
            }
          </select>
        </div>
      </div>

      <!-- Linked Contact / Lead (Optional) -->
      <div class="mb-3">
        <label class="form-label fs-8 fw-semibold text-dark">
          Link to Contact / Lead <span class="text-muted fw-normal fs-9">(Optional - leave blank for internal company task)</span>
        </label>
        <lib-contact-selector 
          [(ngModel)]="formContactId" 
          name="formContactId" 
          [placeholder]="'Select Contact or Lead (Optional)'"
          [clearable]="true"
          [accountStatus]="'all'">
        </lib-contact-selector>
      </div>

      <!-- Description / Agenda Notes -->
      <div class="mb-3">
        <label class="form-label fs-8 fw-semibold text-dark">
          Description & Follow-Up Agenda
        </label>
        <textarea 
          class="form-control form-control-sm" 
          rows="3" 
          placeholder="Enter details, agreed terms, questions to ask, or inspection checklist..." 
          [(ngModel)]="formDescription" 
          name="formDescription">
        </textarea>
      </div>

      <div class="d-flex align-items-center justify-content-end gap-2 pt-2 border-top">
        <button type="button" class="btn btn-sm btn-light border" (click)="modal.dismiss()">Cancel</button>
        <button type="submit" class="btn btn-sm btn-primary d-inline-flex align-items-center" [disabled]="createSubmitting">
          @if (createSubmitting) {
            <span class="spinner-border spinner-border-sm me-1" role="status"></span>
            <span>Scheduling...</span>
          } @else {
            <i class="bi bi-check-lg me-1"></i>
            <span>Schedule Reminder</span>
          }
        </button>
      </div>
    </form>
  </div>
</ng-template>


<!-- ========================================================================= -->
<!-- MODAL: ACTIONS (COMPLETE, RESCHEDULE, CANCEL) -->
<!-- ========================================================================= -->
<ng-template #actionModal let-modal>
  <div class="modal-header" [ngClass]="{
    'bg-success-subtle text-success': actionType === 'complete',
    'bg-primary-subtle text-primary': actionType === 'reschedule',
    'bg-danger-subtle text-danger': actionType === 'cancel'
  }">
    <h6 class="modal-title fw-bold d-flex align-items-center gap-2 mb-0">
      @if (actionType === 'complete') {
        <i class="bi bi-check-circle-fill"></i>
        <span>Complete Follow-Up</span>
      } @else if (actionType === 'reschedule') {
        <i class="bi bi-calendar2-range-fill"></i>
        <span>Reschedule Reminder</span>
      } @else {
        <i class="bi bi-x-circle-fill"></i>
        <span>Cancel Reminder</span>
      }
    </h6>
    <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body p-4">
    <!-- Reminder Summary Header -->
    @if (selectedReminder) {
      <div class="p-3 bg-light rounded-2 border mb-3">
        <div class="fw-bold text-dark fs-7">{{ selectedReminder.title }}</div>
        <div class="d-flex align-items-center gap-3 fs-8 text-muted mt-1">
          <span><i class="bi bi-clock me-1"></i>Due: {{ selectedReminder.due_at | date:'dd MMM yyyy, hh:mm a' }}</span>
          @if (selectedReminder.contact) {
            <span><i class="bi bi-person me-1"></i>{{ selectedReminder.contact.name }}</span>
          }
        </div>
      </div>
    }

    @if (actionErrorMessage) {
      <div class="alert alert-danger py-2 fs-8 mb-3">
        <i class="bi bi-exclamation-circle me-1"></i>{{ actionErrorMessage }}
      </div>
    }

    <form (ngSubmit)="submitAction(modal)">
      <!-- 1. Outcome (if Complete) -->
      @if (actionType === 'complete') {
        <div class="mb-3">
          <label class="form-label fs-8 fw-semibold text-dark">
            Follow-Up Outcome <span class="text-danger">*</span>
          </label>
          <select class="form-select form-select-sm" [(ngModel)]="actionOutcome" name="actionOutcome" required>
            @for (opt of outcomeOptions; track opt) {
              <option [value]="opt">{{ opt }}</option>
            }
          </select>
        </div>
      }

      <!-- 2. Next Follow-Up (if Reschedule) -->
      @if (actionType === 'reschedule') {
        <div class="mb-3">
          <label class="form-label fs-8 fw-semibold text-dark">
            New Follow-Up Date & Time <span class="text-danger">*</span>
          </label>
          <input 
            type="datetime-local" 
            class="form-control form-control-sm" 
            [(ngModel)]="actionNextFollowupAt" 
            name="actionNextFollowupAt" 
            required>
        </div>
      }

      <!-- 3. Notes / Reason (Always required) -->
      <div class="mb-3">
        <label class="form-label fs-8 fw-semibold text-dark">
          @if (actionType === 'complete') {
            Resolution Notes / Summary <span class="text-danger">*</span>
          } @else if (actionType === 'reschedule') {
            Reason for Rescheduling <span class="text-danger">*</span>
          } @else {
            Reason for Cancellation <span class="text-danger">*</span>
          }
        </label>
        <textarea 
          class="form-control form-control-sm" 
          rows="3" 
          [placeholder]="actionType === 'complete' ? 'Client asked for invoice, deal confirmed...' : 'Customer postponed meeting...'"
          [(ngModel)]="actionComment" 
          name="actionComment" 
          required 
          autofocus>
        </textarea>
      </div>

      <div class="d-flex align-items-center justify-content-end gap-2 pt-2 border-top">
        <button type="button" class="btn btn-sm btn-light border" (click)="modal.dismiss()">Back</button>
        <button 
          type="submit" 
          class="btn btn-sm" 
          [ngClass]="{
            'btn-success': actionType === 'complete',
            'btn-primary': actionType === 'reschedule',
            'btn-danger': actionType === 'cancel'
          }"
          [disabled]="actionSubmitting">
          @if (actionSubmitting) {
            <span class="spinner-border spinner-border-sm me-1" role="status"></span>
            <span>Recording...</span>
          } @else {
            <span>Confirm {{ actionType | titlecase }}</span>
          }
        </button>
      </div>
    </form>
  </div>
</ng-template>


<!-- ========================================================================= -->
<!-- MODAL: ACTION HISTORY & DETAILS -->
<!-- ========================================================================= -->
<ng-template #historyModal let-modal>
  <div class="modal-header bg-light">
    <div>
      <h6 class="modal-title fw-bold text-dark d-flex align-items-center gap-2 mb-0">
        <i class="bi bi-clock-history text-primary"></i>
        <span>Follow-Up Audit History & Details</span>
      </h6>
      <span class="fs-9 text-muted">Complete timeline of action events and resolutions</span>
    </div>
    <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body p-4">
    @if (loadingHistory) {
      <div class="text-center py-4 text-muted">
        <div class="spinner-border spinner-border-sm text-primary mb-2" role="status"></div>
        <div class="fs-8">Loading history trail...</div>
      </div>
    }

    @if (historyReminder) {
      <!-- Details Header Card -->
      <div class="app-card p-3 mb-3 bg-light-subtle">
        <div class="d-flex align-items-start justify-content-between gap-2">
          <div>
            <h6 class="fw-bold text-dark mb-1">{{ historyReminder.title }}</h6>
            @if (historyReminder.description) {
              <p class="fs-8 text-muted mb-2">{{ historyReminder.description }}</p>
            }
          </div>
          <span class="badge" [ngClass]="{
            'bg-warning-subtle text-warning-emphasis': historyReminder.reminder_status === 'pending',
            'bg-success-subtle text-success': historyReminder.reminder_status === 'completed',
            'bg-secondary-subtle text-secondary': historyReminder.reminder_status === 'cancelled'
          }">
            {{ historyReminder.reminder_status | titlecase }}
          </span>
        </div>

        <div class="row g-2 fs-8 pt-2 border-top">
          <div class="col-sm-6">
            <span class="text-muted"><i class="bi bi-clock me-1 text-primary"></i>Due: </span>
            <strong class="text-dark">{{ historyReminder.due_at | date:'dd MMM yyyy, hh:mm a' }}</strong>
          </div>
          <div class="col-sm-6">
            <span class="text-muted"><i class="bi bi-person-badge me-1 text-primary"></i>Assigned: </span>
            <strong class="text-dark">{{ getRepName(historyReminder.assigned_user) }}</strong>
          </div>
          @if (historyReminder.contact) {
            <div class="col-sm-12">
              <span class="text-muted"><i class="bi bi-link-45deg me-1 text-primary"></i>Linked Contact: </span>
              <a [routerLink]="['/hrm', historyReminder.contact.contact_type?.contact_type_code || 'customer', 'view', historyReminder.contact.contact_id]" (click)="modal.dismiss()" class="text-primary fw-semibold">
                {{ historyReminder.contact.name }} ({{ historyReminder.contact.phone || 'No phone' }})
              </a>
            </div>
          }
        </div>
      </div>

      <!-- Action History Timeline -->
      <h6 class="fw-bold fs-8 text-uppercase text-muted mb-2">Action Event Timeline</h6>
      @if (historyReminder.actions && historyReminder.actions.length > 0) {
        <div class="timeline-container ps-3 border-start">
          @for (act of historyReminder.actions; track act.action_id || $index) {
            <div class="timeline-item mb-3 position-relative ps-3">
              <div class="timeline-dot" [ngClass]="{
                'bg-success': act.action_type === 'complete',
                'bg-primary': act.action_type === 'reschedule',
                'bg-danger': act.action_type === 'cancel',
                'bg-secondary': act.action_type === 'comment'
              }"></div>

              <div class="d-flex align-items-center justify-content-between">
                <span class="fw-bold text-dark fs-8 text-capitalize">
                  {{ act.action_type }}
                  @if (act.outcome) {
                    <span class="badge bg-success-subtle text-success ms-1">{{ act.outcome }}</span>
                  }
                </span>
                <span class="fs-9 text-muted">{{ act.occurred_at | date:'dd MMM yyyy, hh:mm a' }}</span>
              </div>

              @if (act.comment) {
                <div class="fs-8 text-dark bg-white p-2 rounded border mt-1">
                  {{ act.comment }}
                </div>
              }

              @if (act.next_followup_at) {
                <div class="fs-9 text-primary mt-1">
                  <i class="bi bi-arrow-return-right me-1"></i>Next Follow-Up set to: <strong>{{ act.next_followup_at | date:'dd MMM yyyy, hh:mm a' }}</strong>
                </div>
              }

              <div class="fs-9 text-muted mt-1">
                By: {{ act.performed_by_user?.firstname }} {{ act.performed_by_user?.lastname || '' }}
              </div>
            </div>
          }
        </div>
      } @else {
        <div class="text-center py-3 text-muted fs-8">
          No actions recorded yet on this reminder.
        </div>
      }
    }
  </div>
  <div class="modal-footer bg-light p-2">
    <button type="button" class="btn btn-sm btn-secondary" (click)="modal.dismiss()">Close</button>
  </div>
</ng-template>
